import React, { useCallback, useMemo } from 'react'
import { usePantryStore } from '../../../providers/PantryProvider'
import { useLoyaltyRulesQuery } from '../hooks/useLoyalty'
import { LoyaltyContext } from './LoyaltyContext'

interface LoyaltyProviderProps {
  children: React.ReactNode
}

/**
 * Page-level provider for the Loyalty module.
 *
 * Exposes:
 *  - the active `storeId`, and
 *  - the loyalty rules document, fetched eagerly on page mount so the Points
 *    tab editor and the member-drawer tier-progress card both render with
 *    data already in cache.
 *
 * Tab-specific state (e.g. the members list query, the activity feed) still
 * lives in each tab's own provider.
 */
export const LoyaltyProvider: React.FC<LoyaltyProviderProps> = ({
  children,
}) => {
  const store = usePantryStore()
  const storeId = store?.storeId ?? ''

  const {
    data: rules,
    isLoading: rulesLoading,
    isError: rulesError,
    refetch,
  } = useLoyaltyRulesQuery(storeId)

  const refetchRules = useCallback(() => {
    refetch()
  }, [refetch])

  const value = useMemo(
    () => ({
      storeId,
      rules,
      rulesLoading,
      rulesError,
      refetchRules,
    }),
    [storeId, rules, rulesLoading, rulesError, refetchRules],
  )

  return (
    <LoyaltyContext.Provider value={value}>{children}</LoyaltyContext.Provider>
  )
}
